<!-- src/components/trends/TrialFunnelPanel.vue -->
<!-- 회원권 추세 "체험 전환" 탭: 무료체험 신청 → 등록 깔때기·월별 표·자동 예약 실패·참석 후 미등록 -->
<!-- (docs/architecture/admin-trends-design.md 1.6·2.3·4.5절). 불러오기·오류는 TrendsView가, 이 패널은 받은 값만 그린다 -->
<template>
  <div class="tf-panel">
    <div v-if="!data" class="state">불러오는 중…</div>

    <div v-else-if="isEmpty" class="state">
      고른 기간에 무료체험 신청과 드랍인이 없습니다.
    </div>

    <template v-else>
      <!-- 타일: 등록 집계가 끝난 달의 합계(totals). 집계 중인 달은 아래 한 줄(open_totals) -->
      <div class="tiles">
        <div class="tile">
          <span class="tile-k">확정 등록률</span>
          <span class="tile-v" :class="{ small: regSmall }">{{
            hasClosed ? pct(totals.registration_rate) : "—"
          }}</span>
          <span class="tile-sub">
            {{
              hasClosed
                ? fracText(totalFunnel.registered, totalFunnel.applied, "명")
                : ""
            }}
            <span v-if="hasClosed && regSmall" class="small-tag">{{
              smallSampleLabel
            }}</span>
          </span>
          <span class="tile-def">{{ shortDef("registration_rate") }}</span>
        </div>

        <div class="tile">
          <span class="tile-k">체험 참석 → 등록</span>
          <span class="tile-v" :class="{ small: attSmall }">{{
            hasClosed ? pct(totals.attended_registration_rate) : "—"
          }}</span>
          <span class="tile-sub">
            {{
              hasClosed
                ? fracText(
                    totals.registered_after_attended,
                    attendedBase(totals),
                    "명"
                  )
                : ""
            }}
            <span v-if="hasClosed && attSmall" class="small-tag">{{
              smallSampleLabel
            }}</span>
          </span>
          <span class="tile-def">{{
            shortDef("attended_registration_rate")
          }}</span>
        </div>

        <div class="tile">
          <span class="tile-k">등록까지 평균</span>
          <span
            class="tile-v"
            :class="{ small: hasClosed && daysSmall(totals) }"
            >{{ hasClosed ? daysText(totals.avg_days_to_register) : "—" }}</span
          >
          <span class="tile-sub"
            >{{
              hasClosed && isNum(totals.median_days_to_register)
                ? `중앙값 ${daysText(totals.median_days_to_register)}`
                : ""
            }}
            <span v-if="hasClosed && daysSmall(totals)" class="small-tag">{{
              smallSampleLabel
            }}</span></span
          >
          <span class="tile-def">{{ shortDef("avg_days_to_register") }}</span>
        </div>

        <div class="tile">
          <span class="tile-k">기존 회원</span>
          <span class="tile-v"
            >{{ hasClosed ? count(totals.existing_members) : "—"
            }}<small v-if="hasClosed">명</small></span
          >
          <span class="tile-sub">{{
            hasClosed ? "전환율에서 뺐습니다" : ""
          }}</span>
          <span class="tile-def">신청할 때 이미 수강권이 있던 사람</span>
        </div>
      </div>

      <p class="tf-scope">
        <span>{{ throughLine }}</span>
        <span v-if="openLine" class="open-line">{{ openLine }}</span>
      </p>

      <!-- 깔때기: 가로 막대(한 색, 길이가 크기), 단계마다 수·신청 대비·앞 단계 대비 -->
      <section class="tf-section" aria-label="체험 → 등록 깔때기">
        <div class="section-head">
          <h3 class="section-title">체험 → 등록</h3>
          <span class="section-note"
            >{{ hasClosed ? throughShort : "" }}뒤 단계 사람은 앞 단계 수에도
            듭니다</span
          >
          <span v-if="hasClosed && funnelSmall" class="small-tag">{{
            smallSampleLabel
          }}</span>
        </div>

        <p v-if="!hasClosed" class="tf-note-box">
          아직 등록 집계가 끝난 달이 없습니다. 달마다의 수는 아래 표에 있습니다.
        </p>

        <ol v-else class="funnel">
          <li
            v-for="(s, i) in funnel"
            :key="s.stage"
            class="fn-step"
            :class="`fn-step--${s.stage}`"
          >
            <span class="fn-label">{{ s.label }}</span>
            <span class="fn-track" aria-hidden="true">
              <span class="fn-bar" :style="barStyle(s)"></span>
            </span>
            <span class="fn-count">{{ count(s.count) }}</span>
            <span class="fn-rates" :class="{ small: funnelSmall }">
              <template v-if="i === 0">{{
                isNum(s.count) && s.count > 0 ? "100%" : "—"
              }}</template>
              <template v-else>
                신청의 {{ pct(s.ofApplied) }}
                <span class="sep">·</span>
                앞 단계의 {{ pct(s.ofPrev) }}
                <span v-if="isSmallSample(prevCount(i))" class="frac">{{
                  fracText(s.count, prevCount(i))
                }}</span>
              </template>
            </span>
            <span v-if="s.stage === 'attended'" class="fn-note"
              >참석완료와 등록한 사람 · 바로 등록
              {{ count(totals.registered_direct) }}명 포함</span
            >
            <span v-else-if="s.stage === 'registered'" class="fn-note"
              >참석 뒤 {{ count(totals.registered_after_attended) }} · 바로
              {{ count(totals.registered_direct) }}</span
            >
          </li>
        </ol>

        <p v-if="hasClosed" class="fn-extra">
          취소 <b>{{ count(totals.cancelled) }}</b
          >명
          <span class="fn-extra-def">{{ shortDef("cancelled") }}</span>
        </p>
      </section>

      <!-- 월별 표: 최신 달이 위. 비율 아래 (분자/분모) -->
      <section class="tf-section" aria-label="월별 체험 전환">
        <div class="section-head">
          <h3 class="section-title">월별</h3>
          <span class="section-note"
            >최신 달이 위 · 비율 아래 (등록/분모) · 신청 뒤 60일이 안 지난 달은
            등록 집계 중</span
          >
        </div>

        <div class="table-wrap" role="region" aria-label="월별 표" tabindex="0">
          <table class="tf-table">
            <thead>
              <tr>
                <th scope="col" class="col-month">월</th>
                <th scope="col">신청</th>
                <th scope="col">기존 회원</th>
                <th scope="col">
                  연락<span class="th-sub">뒤 단계 포함</span>
                </th>
                <th scope="col">
                  체험 예약<span class="th-sub">뒤 단계 포함</span>
                </th>
                <th scope="col">
                  체험 참석<span class="th-sub">뒤 단계 포함</span>
                </th>
                <th scope="col">
                  등록<span class="th-sub">참석 뒤 · 바로</span>
                </th>
                <th scope="col">등록률</th>
                <th scope="col">참석 → 등록</th>
                <th scope="col">취소</th>
                <th scope="col">자동 예약 실패</th>
                <th scope="col">
                  등록까지<span class="th-sub">평균 · 중앙값</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in rowsDesc"
                :key="r.month"
                :class="{ 'no-data': r.no_data }"
              >
                <th scope="row" class="col-month">
                  <span class="month">{{ monthLabel(r.month) }}</span>
                  <span v-if="r.in_progress" class="badge">{{
                    inProgressLabel
                  }}</span>
                  <span v-if="r.no_data" class="badge">{{ noDataLabel }}</span>
                  <span v-else-if="r.registration_open" class="badge"
                    >등록 {{ openLabel }}</span
                  >
                </th>
                <!-- 기록 전 달은 신청에서 나오는 수(신청·연락·체험·취소·자동 예약 실패)만 보이고, 수강권 기록이 있어야 아는
                     칸(기존 회원·등록·두 비율·등록까지)은 "—"(R31, 리뷰 3차: 기록 전 등록률 "0.0% 0/5"가 "아무도 등록하지
                     않았다"로 읽혔다) -->
                <td :title="requestsTitle(r)">
                  {{ count(funnelOf(r).applied) }}
                </td>
                <td>{{ r.no_data ? "—" : count(r.existing_members) }}</td>
                <td>{{ count(funnelOf(r).contacted) }}</td>
                <td>{{ count(funnelOf(r).scheduled) }}</td>
                <td>
                  <div>{{ count(funnelOf(r).attended) }}</div>
                  <div v-if="!r.no_data && r.registered_direct > 0" class="sub">
                    바로 등록 {{ count(r.registered_direct) }} 포함
                  </div>
                </td>
                <td v-if="r.no_data">—</td>
                <td v-else>
                  <div>{{ count(funnelOf(r).registered) }}</div>
                  <div v-if="funnelOf(r).registered > 0" class="sub">
                    {{ count(r.registered_after_attended) }} ·
                    {{ count(r.registered_direct) }}
                  </div>
                </td>
                <td v-if="r.no_data" class="rate">—</td>
                <td
                  v-else
                  class="rate"
                  :class="{
                    small: isSmallSample(funnelOf(r).applied),
                    open: r.registration_open,
                  }"
                >
                  <div>{{ pct(r.registration_rate) }}</div>
                  <div
                    v-if="
                      rateSub(r, funnelOf(r).registered, funnelOf(r).applied)
                    "
                    class="sub"
                  >
                    {{
                      rateSub(r, funnelOf(r).registered, funnelOf(r).applied)
                    }}
                  </div>
                </td>
                <td v-if="r.no_data" class="rate">—</td>
                <td
                  v-else
                  class="rate"
                  :class="{
                    small: isSmallSample(attendedBase(r)),
                    open: r.registration_open,
                  }"
                >
                  <div>{{ pct(r.attended_registration_rate) }}</div>
                  <div
                    v-if="
                      rateSub(r, r.registered_after_attended, attendedBase(r))
                    "
                    class="sub"
                  >
                    {{
                      rateSub(r, r.registered_after_attended, attendedBase(r))
                    }}
                  </div>
                </td>
                <td>{{ count(r.cancelled) }}</td>
                <td :title="failuresTitle(r.failures)">
                  <div>{{ count(failureTotal(r.failures)) }}</div>
                  <div
                    v-for="f in nonZeroFailures(r.failures)"
                    :key="f.reason"
                    class="sub"
                  >
                    {{ f.label }} {{ count(f.count) }}
                  </div>
                </td>
                <td v-if="r.no_data">—</td>
                <!-- 등록 집계 중인 달(리뷰 6차: 덜 모인 값이 확정값처럼 밝았다)과 등록한 사람이 5명보다 적은 달(리뷰 5차)의
                     평균·중앙값은 비율 칸처럼 회색 + 까닭(집계 중이 먼저). 까닭은 칸이 좁으면 중앙값 아래 줄로 내려간다
                     (리뷰 6차: 1366px에서 "· 표본 적음"이 표를 상자 밖으로 밀어 이 칸이 잘렸다) -->
                <td v-else class="days" :class="{ small: !!daysNote(r) }">
                  <div>{{ daysText(r.avg_days_to_register) }}</div>
                  <div v-if="isNum(r.median_days_to_register)" class="sub">
                    {{ daysSub(r) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="tf-foot">
          연락·체험 예약·체험 참석은 그 단계까지 간 사람에 뒤 단계(등록 포함)의
          사람을 더한 수라 체험 없이 바로 등록한 사람도 듭니다. 참석 → 등록은 그
          사람을 뺀 참석완료만 분모로 씁니다. 비율이 회색이면 분모가
          {{ smallSampleMin }}보다 작아 한두 명으로 크게 흔들리거나({{
            smallSampleLabel
          }}), 신청 뒤 60일이 안 지나 등록 수가 더 늘 수 있습니다({{
            openLabel
          }}). 등록까지 일수도 등록 집계 중인 달({{ openLabel }})과 등록한
          사람이 {{ smallSampleMin }}명보다 적은 달({{ smallSampleLabel }})은
          회색입니다. 등록은 전화번호가 같은 회원으로 찾습니다.
        </p>
      </section>

      <!-- 드랍인: 체험 깔때기에서 뺀 방문(설계 1.6절 "드랍인"). 최신 달이 위 -->
      <section class="tf-section" aria-label="드랍인">
        <div class="section-head">
          <h3 class="section-title">드랍인</h3>
          <span class="section-note">{{ dropTotalsLine }}</span>
        </div>
        <p class="section-def">{{ definitionOf("drop_ins") }}</p>
        <div
          class="table-wrap"
          role="region"
          aria-label="월별 드랍인 표"
          tabindex="0"
        >
          <table class="tf-table">
            <thead>
              <tr>
                <th scope="col" class="col-month">월</th>
                <th scope="col">드랍인<span class="th-sub">방문</span></th>
                <th scope="col">사람</th>
                <th scope="col">
                  무료체험 뒤<span class="th-sub">참석했던 사람</span>
                </th>
                <th scope="col">기존 회원</th>
                <th scope="col" :title="definitionOf('drop_in_registered')">
                  등록<span class="th-sub">60일 안</span>
                </th>
                <th scope="col">등록률</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in dropRowsDesc"
                :key="r.month"
                :class="{ 'no-data': r.no_data }"
              >
                <th scope="row" class="col-month">
                  <span class="month">{{ monthLabel(r.month) }}</span>
                  <span v-if="r.in_progress" class="badge">{{
                    inProgressLabel
                  }}</span>
                  <span v-if="r.no_data" class="badge">{{ noDataLabel }}</span>
                  <span v-else-if="r.registration_open" class="badge"
                    >등록 {{ openLabel }}</span
                  >
                </th>
                <td>{{ count(r.visits) }}</td>
                <td>{{ count(r.people) }}</td>
                <td>{{ count(r.after_trial) }}</td>
                <td>{{ r.no_data ? "—" : count(r.existing_members) }}</td>
                <td>{{ r.no_data ? "—" : count(r.registered) }}</td>
                <td v-if="r.no_data" class="rate">—</td>
                <td
                  v-else
                  class="rate"
                  :class="{
                    small: isSmallSample(r.people),
                    open: r.registration_open,
                  }"
                >
                  <div>{{ pct(r.registration_rate) }}</div>
                  <div v-if="rateSub(r, r.registered, r.people)" class="sub">
                    {{ rateSub(r, r.registered, r.people) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="tf-foot">{{ definitionOf("drop_in_registered") }}</p>
      </section>

      <!-- 자동 예약 실패: 고른 기간 전체(달마다 합), 신청 건수 -->
      <section class="tf-section" aria-label="자동 예약 실패">
        <div class="section-head">
          <h3 class="section-title">자동 예약 실패</h3>
          <span class="section-note"
            >고른 기간 {{ monthsText }} 합계 · 신청 건수(기존 회원 신청
            포함)</span
          >
        </div>
        <ul class="fail-list">
          <li v-for="f in periodFailures" :key="f.reason" class="fail-item">
            <span class="fail-k">{{ f.label }}</span>
            <span class="fail-v" :class="{ zero: !f.count }"
              >{{ count(f.count) }}<small>건</small></span
            >
          </li>
        </ul>
      </section>

      <!-- 참석 후 미등록: 다시 연락할 사람(최대 50명). 없으면 칸을 숨긴다 -->
      <section
        v-if="followUps.length"
        class="tf-section"
        aria-label="체험 참석 후 미등록"
      >
        <div class="section-head">
          <h3 class="section-title">
            최근 30일 체험 참석 후 미등록 {{ followUps.length }}명
          </h3>
          <router-link to="/free-trial-requests" class="fu-link"
            >체험 고객 관리 →</router-link
          >
        </div>
        <p class="section-def">{{ definitionOf("follow_ups") }}</p>
        <ul class="fu-list">
          <li v-for="f in followUps" :key="f.request_id" class="fu-item">
            <span class="fu-name">{{ f.name || "이름 없음" }}</span>
            <span class="fu-phone">{{
              prettyPhone(f.phone_number) || "—"
            }}</span>
            <span class="fu-date">신청 {{ shortDate(f.requested_at) }}</span>
            <router-link
              class="fu-link"
              :to="{
                path: '/free-trial-requests',
                query: { request_id: f.request_id },
              }"
            >
              관리하기 →
            </router-link>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import { prettyPhone, shortDate } from "@/lib/memberNotes";
import {
  FAILURE_REASONS,
  IN_PROGRESS_LABEL,
  NO_DATA_LABEL,
  OPEN_LABEL,
  SERIES_COLORS,
  SMALL_SAMPLE_LABEL,
  SMALL_SAMPLE_MIN,
  count,
  definitionOf,
  funnelSteps,
  isSmallSample,
  monthLabel,
  monthLabelShort,
  num1,
  pct,
  pick,
  shortDefinitionOf,
} from "@/lib/trends";

function isNum(x) {
  return typeof x === "number" && Number.isFinite(x);
}

// 응답 failures([{ reason, count }])를 고정 순서(수업 없음·정원 마감·체험 자리 없음)로. 없는 칸은 0
function failuresInOrder(list) {
  return FAILURE_REASONS.map((f) => {
    const found = pick(list, "reason", f.value);
    return {
      reason: f.value,
      label: f.label,
      count: found && isNum(found.count) ? found.count : 0,
    };
  });
}

// "2026-08"·"2026-10" → "8~10월", 한 달이면 "10월"
function monthRangeText(months) {
  if (!Array.isArray(months) || months.length === 0) return "";
  const first = monthLabelShort(months[0]);
  const last = monthLabelShort(months[months.length - 1]);
  if (months.length === 1 || first === last) return first;
  return `${first.replace("월", "")}~${last}`;
}

export default {
  name: "TrialFunnelPanel",
  props: {
    // GET /v1/admin/trends/trials 응답(설계 2.3절)
    data: { type: Object, default: null },
  },
  data() {
    return {
      smallSampleLabel: SMALL_SAMPLE_LABEL,
      smallSampleMin: SMALL_SAMPLE_MIN,
      inProgressLabel: IN_PROGRESS_LABEL,
      noDataLabel: NO_DATA_LABEL,
      openLabel: OPEN_LABEL,
    };
  },
  computed: {
    rows() {
      return this.data && Array.isArray(this.data.rows) ? this.data.rows : [];
    },
    // 표는 최신 달이 위(응답은 오래된 달 → 이번 달)
    rowsDesc() {
      return [...this.rows].reverse();
    },
    followUps() {
      return this.data && Array.isArray(this.data.follow_ups)
        ? this.data.follow_ups
        : [];
    },
    // 드랍인 줄(응답은 오래된 달 → 이번 달). 옛 API처럼 drop_ins가 없으면 빈 배열
    dropRows() {
      const d = this.data && this.data.drop_ins;
      return d && Array.isArray(d.rows) ? d.rows : [];
    },
    dropRowsDesc() {
      return [...this.dropRows].reverse();
    },
    // "7월까지 등록 집계가 끝난 달: 12명 중 3명 등록 (25.0%)"
    dropTotalsLine() {
      const t = this.data && this.data.drop_ins && this.data.drop_ins.totals;
      if (!t || !t.through)
        return "최신 달이 위 · 아직 등록 집계가 끝난 달이 없습니다.";
      return `최신 달이 위 · ${monthLabelShort(
        t.through
      )}까지 등록 집계가 끝난 달: ${count(t.people)}명 중 ${count(
        t.registered
      )}명 등록 (${pct(t.registration_rate)})`;
    },
    isEmpty() {
      const requests = this.rows.reduce(
        (sum, r) => sum + (isNum(r.requests) ? r.requests : 0),
        0
      );
      const drops = this.dropRows.reduce(
        (sum, r) =>
          sum +
          (isNum(r.visits) ? r.visits : 0) +
          (isNum(r.existing_members) ? r.existing_members : 0),
        0
      );
      return requests === 0 && drops === 0 && this.followUps.length === 0;
    },
    totals() {
      return (this.data && this.data.totals) || {};
    },
    totalFunnel() {
      return this.totals.funnel || {};
    },
    // 등록 집계가 끝난 달이 하나라도 있음(through가 ""면 없음)
    hasClosed() {
      return !!this.totals.through;
    },
    regSmall() {
      return isSmallSample(this.totalFunnel.applied);
    },
    attSmall() {
      return isSmallSample(this.attendedBase(this.totals));
    },
    funnelSmall() {
      return isSmallSample(this.totalFunnel.applied);
    },
    funnel() {
      return funnelSteps(this.totalFunnel);
    },
    // "7월까지 등록 집계가 끝난 9개월 합계"
    throughLine() {
      if (!this.hasClosed) return "아직 등록 집계가 끝난 달이 없습니다.";
      const through = monthLabelShort(this.totals.through);
      const n = this.totals.months_counted;
      return isNum(n)
        ? `${through}까지 등록 집계가 끝난 ${n}개월 합계입니다.`
        : `${through}까지 등록 집계가 끝난 달의 합계입니다.`;
    },
    throughShort() {
      return `${monthLabelShort(this.totals.through)}까지 · `;
    },
    // "8~10월 집계 중: 85명 중 18명 등록"
    openLine() {
      const o = this.data && this.data.open_totals;
      if (!o) return "";
      const range = monthRangeText(o.months);
      if (!range) return "";
      return `${range} ${OPEN_LABEL}: ${count(o.applied)}명 중 ${count(
        o.registered
      )}명 등록`;
    },
    // 자동 예약 실패의 기간 합계: 고른 기간의 모든 달(집계 중인 달 포함). 등록과 상관없는 수라 닫힌 달만 더한 totals 대신 줄을 더한다
    periodFailures() {
      const sums = failuresInOrder([]);
      for (const r of this.rows) {
        failuresInOrder(r.failures).forEach((f, i) => {
          sums[i].count += f.count;
        });
      }
      return sums;
    },
    monthsText() {
      const m = this.data && this.data.months;
      return isNum(m) ? `${m}개월` : "";
    },
  },
  methods: {
    prettyPhone,
    shortDate,
    count,
    pct,
    monthLabel,
    definitionOf,
    isSmallSample,
    isNum,

    shortDef(key) {
      return shortDefinitionOf(key);
    },
    funnelOf(r) {
      return (r && r.funnel) || {};
    },
    // 등록까지 일수(평균·중앙값)의 표본 = 등록한 사람. 5명보다 적으면 회색 + 표본 적음(비율과 같은 규칙, R15 (3))
    daysSmall(r) {
      return (
        !!r &&
        isNum(r.avg_days_to_register) &&
        isSmallSample(this.funnelOf(r).registered)
      );
    },
    // 월별 표 등록까지 칸의 까닭: 등록 집계 중인 달은 "집계 중"(신청 뒤 60일까지 늦게 등록하는 사람이 더 들어와 평균이 오른다),
    // 그 밖에 등록한 사람이 5명보다 적으면 "표본 적음"(rateSub와 같은 차례), 아니면 ""
    daysNote(r) {
      if (!r || !isNum(r.avg_days_to_register)) return "";
      if (r.registration_open) return OPEN_LABEL;
      return this.daysSmall(r) ? SMALL_SAMPLE_LABEL : "";
    },
    // 등록까지 칸 둘째 줄: "3일" 또는 "3일 · 집계 중". 까닭 안의 빈칸은 줄을 바꾸지 않는 빈칸이라 좁으면 "· 집계 중" 통째로 다음 줄
    daysSub(r) {
      const days = this.daysText(r.median_days_to_register);
      const note = this.daysNote(r);
      return note ? `${days} ·\u00a0${note.replace(/ /g, "\u00a0")}` : days;
    },
    // 체험 참석 → 등록률의 분모: 깔때기 체험 참석 − 바로 등록(바로 등록한 사람도 누적 수에 든다, 설계 1.6절)
    attendedBase(r) {
      const f = this.funnelOf(r);
      if (!isNum(f.attended) || !isNum(r.registered_direct)) return null;
      return f.attended - r.registered_direct;
    },
    // (8, 38) → "8/38", 분모가 없거나 0이면 ""
    fracText(num, den, unit = "") {
      if (!isNum(num) || !isNum(den) || den <= 0) return "";
      return `${count(num)}/${count(den)}${unit}`;
    },
    // 월별 표 비율 아래 작은 글: 회색인 까닭을 함께(추세·운영 표와 같이). 등록 집계 중인 달은 "3/7 · 집계 중",
    // 분모 5 미만은 "0/2 · 표본 적음"
    rateSub(r, num, den) {
      const frac = this.fracText(num, den);
      if (!frac) return "";
      if (r && r.registration_open) return `${frac} · ${OPEN_LABEL}`;
      if (isSmallSample(den)) return `${frac} · ${SMALL_SAMPLE_LABEL}`;
      return frac;
    },
    // 깔때기 앞 단계의 수
    prevCount(i) {
      const prev = this.funnel[i - 1];
      return prev ? prev.count : null;
    },
    // 막대 길이 = 신청 대비. 0보다 크면 적어도 2px
    barStyle(step) {
      const applied = this.totalFunnel.applied;
      const ratio =
        isNum(step.count) && isNum(applied) && applied > 0
          ? Math.min(1, step.count / applied)
          : 0;
      return {
        width: `${(ratio * 100).toFixed(1)}%`,
        minWidth: ratio > 0 ? "2px" : "0",
        background: SERIES_COLORS[0],
      };
    },
    daysText(x) {
      return isNum(x) ? `${num1(x).replace(/\.0$/, "")}일` : "—";
    },
    requestsTitle(r) {
      const parts = [];
      if (isNum(r.requests)) parts.push(`신청 ${count(r.requests)}건`);
      if (isNum(r.people)) parts.push(`${count(r.people)}명(기존 회원 포함)`);
      return parts.join(" · ") || null;
    },
    failureTotal(list) {
      return failuresInOrder(list).reduce((sum, f) => sum + f.count, 0);
    },
    nonZeroFailures(list) {
      return failuresInOrder(list).filter((f) => f.count > 0);
    },
    failuresTitle(list) {
      return failuresInOrder(list)
        .map((f) => `${f.label} ${f.count}`)
        .join(" · ");
    },
  },
};
</script>

<style scoped>
.tf-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
  color: #fff;
}

.state {
  padding: 40px 16px;
  border-radius: 10px;
  border: 1px dashed #2a2a2a;
  text-align: center;
  color: #c8c8c8;
  font-size: 15px;
}

/* ---------------- 타일 ---------------- */
.tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #0a0a0a;
}

.tile-k {
  font-size: 13px;
  color: #c8c8c8;
  word-break: keep-all;
}

.tile-v {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
}

.tile-v small {
  margin-left: 2px;
  font-size: 14px;
  font-weight: 400;
  color: #8e8e8e;
}

.tile-v.small {
  color: #9ca3af;
}

.tile-sub {
  min-height: 18px;
  font-size: 13px;
  color: #c8c8c8;
  font-variant-numeric: tabular-nums;
}

/* 보이는 짧은 정의(툴팁 아님) */
.tile-def {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #8e8e8e;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.small-tag {
  display: inline-block;
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid rgba(156, 163, 175, 0.3);
  background: rgba(156, 163, 175, 0.1);
  font-size: 11px;
  line-height: 18px;
  color: #9ca3af;
  white-space: nowrap;
}

.tf-scope {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  margin: -6px 0 0;
  font-size: 13px;
  color: #9d9d9d;
  word-break: keep-all;
}

.open-line {
  color: #c8c8c8;
}

/* ---------------- 칸 공통 ---------------- */
.tf-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.section-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
}

.section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.section-note,
.section-def {
  font-size: 12px;
  color: #8e8e8e;
  word-break: keep-all;
}

.section-def {
  margin: -4px 0 0;
}

.tf-note-box {
  margin: 0;
  padding: 16px;
  border-radius: 8px;
  background: #0a0a0a;
  font-size: 14px;
  color: #c8c8c8;
}

/* ---------------- 깔때기 ---------------- */
.funnel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 14px;
  list-style: none;
  border-radius: 8px;
  background: #0a0a0a;
}

.fn-step {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) 52px minmax(180px, 0.9fr);
  grid-template-areas:
    "label bar count rates"
    ". note note note";
  align-items: center;
  column-gap: 12px;
  row-gap: 2px;
}

.fn-label {
  grid-area: label;
  font-size: 14px;
  color: #e5e5e5;
  white-space: nowrap;
}

.fn-track {
  grid-area: bar;
  display: block;
  height: 18px;
  border-radius: 0 4px 4px 0;
  background: #1b1d1e;
  overflow: hidden;
}

.fn-bar {
  display: block;
  height: 100%;
  border-radius: 0 4px 4px 0;
}

.fn-count {
  grid-area: count;
  font-size: 16px;
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.fn-rates {
  grid-area: rates;
  font-size: 13px;
  color: #c8c8c8;
  font-variant-numeric: tabular-nums;
}

.fn-rates.small {
  color: #9ca3af;
}

.fn-rates .sep {
  margin: 0 2px;
  color: #6b7280;
}

.fn-rates .frac {
  margin-left: 4px;
  color: #9ca3af;
}

.fn-rates .frac::before {
  content: "(";
}

.fn-rates .frac::after {
  content: ")";
}

.fn-note {
  grid-area: note;
  font-size: 12px;
  color: #8e8e8e;
}

.fn-step--registered .fn-label,
.fn-step--registered .fn-count {
  color: #78ff47;
}

.fn-extra {
  margin: 0;
  font-size: 13px;
  color: #c8c8c8;
}

.fn-extra-def {
  margin-left: 8px;
  font-size: 12px;
  color: #8e8e8e;
}

/* ---------------- 월별 표 ---------------- */
/* 넓은 표는 자기 상자 안에서만 가로로 스크롤하고 월 칸은 붙여 둔다(설계 4.2절) */
.table-wrap {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border-radius: 8px;
  background: #0a0a0a;
}

.table-wrap:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

.tf-table {
  width: 100%;
  min-width: 820px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

.tf-table th,
.tf-table td {
  padding: 8px 6px;
  text-align: right;
  vertical-align: top;
  border-bottom: 1px solid #1f1f1f;
  white-space: nowrap;
}

.tf-table thead th {
  font-size: 12px;
  font-weight: 400;
  color: #8e8e8e;
  white-space: normal;
  word-break: keep-all;
  vertical-align: bottom;
  background: #0a0a0a;
}

/* 머리 작은 글("뒤 단계 포함" 등)은 한 덩어리로 줄을 바꾼다: 1200px처럼 표가 최소 너비로 줄면 칸이 가장 좁아져
   "연락 / 뒤 / 단계 / 포함"으로 한 낱말씩 쌓였고 "· 바로"로 시작하는 줄이 생겼다. 색은 #0a0a0a 위 6:1(리뷰 3차: #6b7280은 4.1:1) */
.th-sub {
  display: block;
  font-size: 11px;
  color: #8e8e8e;
  white-space: nowrap;
}

.tf-table .col-month {
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 92px;
  padding-left: 12px;
  text-align: left;
  background: #0a0a0a;
  border-right: 1px solid #1f1f1f;
}

.tf-table thead .col-month {
  z-index: 2;
}

.tf-table tbody th.col-month {
  font-weight: 600;
  color: #fff;
}

.tf-table tbody tr:hover td,
.tf-table tbody tr:hover th {
  background: #121314;
}

.tf-table tbody tr:last-child td,
.tf-table tbody tr:last-child th {
  border-bottom: 0;
}

.month {
  display: block;
}

.badge {
  display: inline-block;
  margin-top: 2px;
  margin-right: 4px;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid rgba(156, 163, 175, 0.3);
  background: rgba(156, 163, 175, 0.1);
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  color: #9ca3af;
  white-space: nowrap;
}

.sub {
  font-size: 12px;
  color: #8e8e8e;
}

/* 분모가 작거나(표본 적음) 등록 집계 중인 달의 비율은 회색 */
.rate.small,
.rate.open {
  color: #9ca3af;
}

/* 기록 전 달의 신청 수도 읽는 숫자라 #0a0a0a 위 6:1(리뷰 5차: #6b7280은 4.1:1) */
.no-data td {
  color: #8e8e8e;
}

/* 등록 집계 중이거나 등록한 사람이 5명보다 적은 달의 등록까지 일수 */
.tf-table td.small {
  color: #9ca3af;
}

/* 등록까지 칸 둘째 줄은 "3일"과 "· 집계 중" 사이에서만 줄을 바꾼다(표가 1366px 상자에 들게, 리뷰 6차) */
.tf-table td.days .sub {
  white-space: normal;
  word-break: keep-all;
}

.tf-foot {
  margin: 0;
  font-size: 12px;
  color: #8e8e8e;
  word-break: keep-all;
}

/* ---------------- 자동 예약 실패 ---------------- */
.fail-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fail-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #0a0a0a;
}

.fail-k {
  font-size: 14px;
  color: #c8c8c8;
}

.fail-v {
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.fail-v.zero {
  color: #6b7280;
}

.fail-v small {
  margin-left: 2px;
  font-size: 13px;
  font-weight: 400;
  color: #8e8e8e;
}

/* ---------------- 참석 후 미등록 ---------------- */
.fu-link {
  margin-left: auto;
  font-size: 13px;
  color: #78ff47;
  text-decoration: none;
}

.fu-link:hover {
  text-decoration: underline;
}

.fu-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fu-item {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #0a0a0a;
  font-size: 14px;
}

.fu-name {
  font-weight: 700;
}

.fu-phone {
  color: #c8c8c8;
  font-variant-numeric: tabular-nums;
}

.fu-date {
  margin-left: auto;
  font-size: 13px;
  color: #9d9d9d;
}

/* ---------------- 반응형 ---------------- */
@media (max-width: 1280px) {
  .tile-v {
    font-size: 20px;
  }
}

@media (max-width: 720px) {
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tile {
    padding: 10px 12px;
  }

  .fail-list {
    grid-template-columns: 1fr;
  }

  /* 깔때기: 이름·수 / 막대 / 비율 / 설명 */
  .fn-step {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "label count"
      "bar bar"
      "rates rates"
      "note note";
    row-gap: 4px;
  }

  .fu-link {
    margin-left: 0;
  }

  /* 붙여 둔 월 칸이 390px 화면의 절반 가까이 먹었다: 배지를 세로로 쌓아 칸을 좁힌다 */
  .tf-table .col-month {
    min-width: 0;
    padding-left: 8px;
    padding-right: 6px;
  }

  .tf-table .badge {
    display: block;
    width: fit-content;
    margin-right: 0;
  }
}
</style>
